import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Trash2, Save, CheckCircle2, MinusCircle, Download, Sparkles, Users, Eye, X,
} from 'lucide-react';

export default function AdminSubscribers() {
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, active: 0, subscribers: [] });
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [filter, setFilter] = useState('active'); // active | unsubscribed | all

  // Preview
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = async () => {
    try {
      const [subs, setg] = await Promise.all([
        api.get('/admin/newsletter/subscribers'),
        api.get('/admin/newsletter/settings'),
      ]);
      setData(subs.data);
      setSettings(setg.data);
    } catch (e) {
      toast.error('Could not load subscribers');
    }
  };

  useEffect(() => { load(); }, []);

  // Escape closes preview modal
  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewOpen]);

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await api.put('/admin/newsletter/settings', {
        welcome_subject: settings.welcome_subject,
        welcome_body: settings.welcome_body,
        welcome_scripture_text: settings.welcome_scripture_text || null,
        welcome_scripture_ref: settings.welcome_scripture_ref || null,
      });
      toast.success('Welcome message saved — new subscribers will see this.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const openPreview = async () => {
    if (!settings?.welcome_subject?.trim() || !settings?.welcome_body?.trim()) {
      toast.error('Add a subject and body first');
      return;
    }
    try {
      setLoadingPreview(true);
      const res = await api.post('/admin/newsletter/preview', {
        welcome_subject: settings.welcome_subject,
        welcome_body: settings.welcome_body,
        welcome_scripture_text: settings.welcome_scripture_text || null,
        welcome_scripture_ref: settings.welcome_scripture_ref || null,
      });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setPreviewOpen(true);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const exportCsv = () => {
    const rows = [['Email', 'Name', 'Status', 'Source', 'Subscribed at']];
    for (const s of data.subscribers) {
      rows.push([
        s.email,
        (s.name || '').replace(/"/g, '""'),
        s.unsubscribed_at ? 'Unsubscribed' : 'Active',
        s.source || '',
        s.subscribed_at || '',
      ]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `love-restored-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = data.subscribers.filter((s) => {
    if (filter === 'active') return !s.unsubscribed_at;
    if (filter === 'unsubscribed') return !!s.unsubscribed_at;
    return true;
  });

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => navigate('/admin')} className="mb-6 inline-flex items-center gap-2 text-sm hover:underline" style={{ color: '#5B7C99', fontFamily: 'Manrope, sans-serif' }} data-testid="admin-subs-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        {/* Hero */}
        <div className="mb-8 p-8 rounded-3xl flex items-center justify-between flex-wrap gap-4" style={{ background: 'linear-gradient(135deg, #F4D67A15 0%, #FDFBF7 60%, #E57A4408 100%)', border: '1px solid #F0EBE0' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', border: '1px solid rgba(255,255,255,0.5)' }}>
              <Mail className="w-6 h-6" style={{ color: '#3D2E0A' }} />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }} data-testid="admin-subs-title">Subscribers</h1>
              <p className="text-sm mt-0.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                People who joined via the floating gold "Subscribe" button on the site.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Stat label="Active" value={data.active} tint="#78A18E" />
            <Stat label="Unsubscribed" value={data.total - data.active} tint="#8C867D" />
            <Stat label="Total" value={data.total} tint="#B8891A" />
          </div>
        </div>

        {/* What they receive */}
        <div className="p-6 rounded-3xl mb-8 flex items-start gap-4" style={{ backgroundColor: '#FBEFCE50', border: '1px solid #E8C26555' }}>
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B8891A' }} />
          <div>
            <p className="font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>What subscribers receive</p>
            <ul className="text-sm space-y-1.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>
              <li>• A warm welcome email the moment they subscribe (editable below)</li>
              <li>• <strong>Weekly Prayer Digest</strong> — the same digest your members get</li>
              <li>• <strong>New article notifications</strong> — any time you publish & notify</li>
              <li>• <strong>Prayer emails broadcast to "All members"</strong> — unless you pick specific members only</li>
              <li>• A one-click Unsubscribe link in every email</li>
            </ul>
          </div>
        </div>

        {/* Welcome message editor */}
        {settings && (
          <div className="p-6 rounded-3xl mb-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              <Mail className="w-5 h-5" style={{ color: '#B8891A' }} /> Welcome email (goes to every new subscriber)
            </h2>
            <Field label="Subject">
              <input value={settings.welcome_subject || ''} onChange={e => setSettings({ ...settings, welcome_subject: e.target.value })} maxLength={140} style={inputStyle} data-testid="welcome-subject" />
            </Field>
            <Field label="Body" hint="Blank lines break paragraphs. The subscriber's first name is auto-greeted at the top.">
              <textarea value={settings.welcome_body || ''} onChange={e => setSettings({ ...settings, welcome_body: e.target.value })} rows={6} maxLength={5000} style={{ ...inputStyle, minHeight: 140, fontFamily: 'Lora, serif', fontSize: 15, lineHeight: 1.7 }} data-testid="welcome-body" />
            </Field>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Field label="Scripture (optional)">
                  <input value={settings.welcome_scripture_text || ''} onChange={e => setSettings({ ...settings, welcome_scripture_text: e.target.value })} maxLength={800} style={inputStyle} placeholder='"The Lord is close to the brokenhearted…"' data-testid="welcome-scripture-text" />
                </Field>
              </div>
              <Field label="Reference">
                <input value={settings.welcome_scripture_ref || ''} onChange={e => setSettings({ ...settings, welcome_scripture_ref: e.target.value })} maxLength={120} placeholder="Psalm 34:18" style={inputStyle} data-testid="welcome-scripture-ref" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 flex-wrap">
              <button onClick={openPreview} disabled={loadingPreview} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-50" style={{ backgroundColor: '#F3EFE7', color: '#2C2724', fontFamily: 'Manrope, sans-serif' }} data-testid="preview-welcome-btn">
                <Eye className="w-4 h-4" /> {loadingPreview ? 'Loading…' : 'Preview'}
              </button>
              <button onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition disabled:opacity-50 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)', color: '#3D2E0A', fontFamily: 'Manrope, sans-serif', boxShadow: '0 4px 14px rgba(201,155,46,0.35)' }} data-testid="save-welcome-btn">
                <Save className="w-4 h-4" /> Save welcome message
              </button>
            </div>
          </div>
        )}

        {/* Subscribers table */}
        <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              <Users className="w-5 h-5" style={{ color: '#78A18E' }} /> The list
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <FilterPill active={filter === 'active'} onClick={() => setFilter('active')} testid="filter-active">Active ({data.active})</FilterPill>
              <FilterPill active={filter === 'unsubscribed'} onClick={() => setFilter('unsubscribed')} testid="filter-unsub">Unsubscribed ({data.total - data.active})</FilterPill>
              <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} testid="filter-all">All ({data.total})</FilterPill>
              <button onClick={exportCsv} disabled={data.total === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition disabled:opacity-40" style={{ backgroundColor: '#F3EFE7', color: '#2C2724', fontFamily: 'Manrope, sans-serif' }} data-testid="export-csv-btn">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
          </div>
          {filtered.length === 0 ? (
            <p className="text-sm italic py-8 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
              {data.total === 0 ? 'No subscribers yet — the floating gold "Subscribe" button appears on every public page.' : 'No subscribers match this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #F0EBE0' }}>
                    <Th>Status</Th><Th>Email</Th><Th>Name</Th><Th>Joined</Th><Th>Source</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.email} className="hover:bg-[#FDFBF7]" style={{ borderBottom: '1px solid #F3EFE7' }} data-testid={`sub-row-${s.email}`}>
                      <Td>
                        {s.unsubscribed_at ? (
                          <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#8C867D' }}>
                            <MinusCircle className="w-3.5 h-3.5" /> Unsubscribed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#3F5641' }}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Active
                          </span>
                        )}
                      </Td>
                      <Td style={{ color: '#2C2A29' }}>{s.email}</Td>
                      <Td style={{ color: '#595553' }}>{s.name || '—'}</Td>
                      <Td style={{ color: '#8C867D' }}>{fmt(s.subscribed_at)}</Td>
                      <Td><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3EFE7', color: '#595553' }}>{s.source || 'floating_popup'}</span></Td>
                      <Td>
                        <button onClick={() => removeSubscriber(s.email)} className="p-1.5 rounded-full transition" style={{ backgroundColor: '#FDECEB' }} data-testid={`delete-sub-${s.email}`}>
                          <Trash2 className="w-3.5 h-3.5" style={{ color: '#B8564A' }} />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {previewOpen && previewHtml && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(44,42,41,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setPreviewOpen(false)}
          data-testid="welcome-preview-modal"
        >
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl" style={{ backgroundColor: '#FDFBF7', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between p-4 border-b" style={{ backgroundColor: '#FDFBF7', borderColor: '#F0EBE0' }}>
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#8C867D', fontWeight: 700, fontFamily: 'Manrope, sans-serif', letterSpacing: '0.1em' }}>Live Preview</p>
                <p className="text-sm font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Subject: {previewSubject}</p>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="p-2 rounded-full hover:bg-[#F3EFE7]" data-testid="welcome-preview-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const inputStyle = { fontFamily: 'Nunito, sans-serif', border: '1px solid #E6E2D8', borderRadius: 12, padding: '10px 14px', width: '100%', backgroundColor: '#FFFFFF', color: '#2C2A29' };

function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}>{label}</label>
      {children}
      {hint && <p className="text-xs mt-1" style={{ color: '#8C867D' }}>{hint}</p>}
    </div>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide" style={{ color: '#8C867D', fontWeight: 600 }}>{label}</p>
      <p className="text-3xl font-bold leading-none mt-1" style={{ fontFamily: 'Merriweather, serif', color: tint }}>{value}</p>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-full text-xs font-semibold transition" style={{ backgroundColor: active ? '#E57A44' : '#F3EFE7', color: active ? '#FFFFFF' : '#2C2724', fontFamily: 'Manrope, sans-serif' }} data-testid={testid}>
      {children}
    </button>
  );
}

function Th({ children }) {
  return <th className="text-left py-2 px-2 text-xs uppercase tracking-wide font-semibold" style={{ color: '#8C867D', letterSpacing: '0.08em' }}>{children}</th>;
}
function Td({ children, style }) {
  return <td className="py-2.5 px-2" style={style}>{children}</td>;
}
